/**
 * Today — the console's whole job on open is to answer "does anything need me?"
 *
 * Everything on this screen is either something waiting for a person, or a number that tells
 * them whether to look further. The counts come from the shadow window, which is a **working
 * view and not the record** — said in the response and repeated here, because the moment a
 * console starts presenting a short unsigned window as an audit trail it has grown the second
 * history that was deliberately deleted.
 */

import { Meter, State, fmtMoney, microToUsd, useAsync } from "@authoxi/ui-shared";
import type { Api } from "../api";

export function Today({ api, onResolved }: { api: Api; onResolved: () => void }) {
  const pending = useAsync(() => api.pending());
  const summary = useAsync(() => api.decisionSummary(24));
  const usage = useAsync(() => api.usage("day"));
  const recent = useAsync(() => api.decisions({ hours: 24, limit: 6 }));

  const waiting = (pending.data?.pending ?? []).filter((p) => p.status === "pending");

  // Closest to a limit — the failure this product actually exists to catch. An agent quietly
  // running past its budget is the common case; a payment awaiting sign-off is the rare one.
  const capped = (usage.data?.rows ?? []).filter((r) => r.limit && r.limit > 0);
  const closest = capped.sort((a, b) => b.used / (b.limit ?? 1) - a.used / (a.limit ?? 1))[0];

  return (
    <>
      <div className="view-head">
        <span className="label">Today</span>
        <h1 data-testid="today-headline">
          {waiting.length
            ? `${waiting.length} ${waiting.length === 1 ? "decision needs" : "decisions need"} you.`
            : "Nothing needs you."}
        </h1>
        <p>
          {waiting.length
            ? "Everything else your agents did today was decided without you, and signed."
            : "Your agents are running inside their mandates. Every decision is being signed and recorded."}
        </p>
      </div>

      {waiting.length > 0 ? (
        <>
          <div className="needs-you">
            <p className="label" style={{ color: "var(--escalate-text)" }}>
              Waiting on a human
            </p>
            <p className="sm" style={{ margin: "var(--s2) 0 0", color: "var(--ink-2)" }}>
              An agent asked, the gate refused to decide alone, and it is holding until you sign.
              Signing uses your key — we never hold it.
            </p>
            <a className="btn btn-go" style={{ marginTop: "var(--s4)" }} href="/console/approvals">
              Review {waiting.length}
            </a>
          </div>
        </>
      ) : (
        pending.data && (
          <div className="all-clear" data-testid="all-clear">
            <p className="md" style={{ margin: "var(--s4) 0 var(--s2)" }}>
              All clear
            </p>
            <p className="sm muted" style={{ margin: 0 }}>
              Nothing is waiting on a person.
            </p>
          </div>
        )
      )}

      <div className="cards-2" style={{ marginTop: "var(--s7)" }}>
        <div className="card pad">
          <p className="label">The last 24 hours</p>
          {summary.loading && <State kind="loading" title="Counting…" />}
          {summary.data && (
            <>
              <div className="row" style={{ marginTop: "var(--s4)", gap: "var(--s6)" }}>
                <div>
                  <div className="md" data-testid="count-total">
                    {summary.data.total}
                  </div>
                  <span className="sm muted">decisions</span>
                </div>
                <div>
                  <div className="md" style={{ color: "var(--deny-text)" }} data-testid="count-deny">
                    {summary.data.deny}
                  </div>
                  <span className="sm muted">refused</span>
                </div>
                <div>
                  <div className="md" style={{ color: "var(--escalate-text)" }}>
                    {summary.data.escalate}
                  </div>
                  <span className="sm muted">escalated</span>
                </div>
                {summary.data.challenge > 0 && (
                  <div>
                    <div className="md" style={{ color: "var(--challenge-text)" }}>
                      {summary.data.challenge}
                    </div>
                    <span className="sm muted">challenged</span>
                  </div>
                )}
              </div>
              {summary.data.shadowed > 0 && (
                <p className="sm" style={{ marginTop: "var(--s3)", color: "var(--escalate-text)" }}>
                  {summary.data.shadowed} went through that an enforcing gate would have held.{" "}
                  <a href="/console/setup">See what changes.</a>
                </p>
              )}
            </>
          )}
        </div>

        <div className="card pad">
          <p className="label">Closest to a limit</p>
          {usage.loading && <State kind="loading" title="Reading the ledger…" />}
          {usage.data && !closest && (
            <p className="sm muted" style={{ marginTop: "var(--s4)" }}>
              No agent holds a cap. Everything is metered and nothing is blocked — which is the
              right default, not a gap.
            </p>
          )}
          {closest && (
            <>
              <p className="sm" style={{ margin: "var(--s4) 0 var(--s2)" }}>
                <span className="mono">{closest.agent_id}</span>
                <span className="muted"> · {closest.resource}</span>
              </p>
              <Meter used={closest.used} limit={closest.limit} />
              <p className="sm muted" style={{ margin: "var(--s2) 0 0" }} data-testid="closest">
                {closest.resource === "inference_usd"
                  ? `${fmtMoney(microToUsd(closest.used))} of ${fmtMoney(microToUsd(closest.limit ?? 0))}`
                  : `${closest.used.toLocaleString()} of ${(closest.limit ?? 0).toLocaleString()}`}{" "}
                today.
              </p>
              <a className="btn" style={{ marginTop: "var(--s4)", width: "100%" }} href="/console/spend">
                Adjust budgets
              </a>
            </>
          )}
        </div>
      </div>

      <div className="rule" style={{ marginTop: "var(--s7)" }}>
        <span className="label">Recent activity</span>
      </div>
      {recent.data && recent.data.decisions.length === 0 && (
        <State
          kind="empty"
          title="Nothing decided in the last day."
          detail="This is a short working window, not the record — see Evidence."
        />
      )}
      <div className="timeline">
        {(recent.data?.decisions ?? []).map((d) => (
          <div className={`tl ${d.decision}`} key={d.observation_id}>
            <div className="tl-time">{d.created_at?.slice(11, 19) ?? ""}</div>
            <div className="tl-what">
              <span className="mono">{d.agent_id}</span> · {d.action_type}
              {d.amount && ` · ${fmtMoney(d.amount, d.currency)}`}
            </div>
            <div className="tl-why">
              {d.reason_code ?? d.decision}
              {/* A deny under dryrun went through anyway. A console that cannot tell those
                  apart reports blocked calls that were not blocked. */}
              {d.enforcement !== "deny" && d.decision !== "allow" && (
                <span className="muted"> · not enforced ({d.enforcement})</span>
              )}
            </div>
          </div>
        ))}
      </div>
      {recent.data && (
        <p className="xs muted" style={{ marginTop: "var(--s3)" }} data-testid="working-view-note">
          {recent.data.window_note}
        </p>
      )}
      <span hidden data-testid="on-resolved-hook" onClick={onResolved} />
    </>
  );
}
